import type { ComponentType } from 'react';
import { Upload } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ToolkitId, DocumentKind } from '@/types/models';
import type { messages as MSG } from '@/lib/i18n.messages';

type Key = keyof typeof MSG;

interface ToolkitTab {
  id: string;
  labelKey: Key;
  /** The page component to render for this sub-tab. */
  Component: ComponentType;
}

export interface ToolkitModule {
  id: ToolkitId;
  labelKey: Key;
  descKey: Key;
  icon: LucideIcon;
  /** Route for the toolkit detail page, e.g. /toolkits/audit */
  toolkitPath: string;
  /** Sub-tabs shown inside /toolkits/:toolkitId */
  toolkitTabs?: ToolkitTab[];
  /** Default sub-tab query param when opening toolkitPath */
  defaultTab?: string;
  /** Component rendered as an onglet in the multi-tab Dashboard */
  DashboardTab?: ComponentType;
  /** Document kinds this toolkit produces (used by Documents section filter) */
  documentKinds?: DocumentKind[];
}

// Lazy imports via dynamic() to avoid circular dependency issues and reduce
// initial bundle size. Components are referenced by their module path.
import { lazy } from 'react';

const UploadAudit     = lazy(() => import('@/pages/UploadAudit'));
const Questionnaire   = lazy(() => import('@/pages/Questionnaire'));
const AuditDashboardTab  = lazy(() =>
  import('../pages/dashboard/AuditDashboardTab').then(m => ({ default: m.AuditDashboardTab }))
);

/** First sub-tab when opening /toolkits/audit (questionnaire before upload). */
export const AUDIT_DEFAULT_TAB = 'questionnaire';

export const TOOLKITS: ToolkitModule[] = [
  {
    id: 'audit',
    labelKey: 'toolkit.audit',
    descKey: 'toolkit.audit.desc',
    icon: Upload,
    toolkitPath: '/toolkits/audit',
    defaultTab: AUDIT_DEFAULT_TAB,
    toolkitTabs: [
      { id: 'questionnaire', labelKey: 'toolkit.audit.tab.questionnaire', Component: Questionnaire },
      { id: 'upload',        labelKey: 'toolkit.audit.tab.upload',        Component: UploadAudit },
    ],
    DashboardTab: AuditDashboardTab,
    documentKinds: ['report', 'survey'],
  },
];

/** Default toolkit IDs when an org has no explicit entitlements configured. */
export const DEFAULT_TOOLKIT_IDS: ToolkitId[] = ['audit'];

/** Find a toolkit by its path segment (e.g. 'audit'). */
export function findToolkitBySlug(slug: string): ToolkitModule | undefined {
  return TOOLKITS.find(tk => tk.toolkitPath === `/toolkits/${slug}`);
}

/** Find a toolkit by its ID. */
export function findToolkitById(id: ToolkitId): ToolkitModule | undefined {
  return TOOLKITS.find(tk => tk.id === id);
}
